import React from 'react';
import {
    hasModifier,
    isIgnorableKeydown,
    type ShortcutScope,
    useIsShortcutScopeActive,
} from '@/components/shortcuts';

type UseDiscountDialogKeysOptions = {
    /** `DISCOUNT_SCOPE`; ignored unless it is the live top of the stack, so the cheat sheet opened over the dialog suspends it. */
    scope: ShortcutScope;
    /** `useDiscountDialog`'s `apply`, which refuses on its own while the value cannot be used. */
    onApply: () => void;
};

/**
 * `Enter` applies the discount from anywhere in the dialog — the value field,
 * the switch, the note (a note is one line, so `Enter` never adds a line
 * break) — unless a button has focus, which activates itself (Cancel, Remove,
 * the close trigger). The same listener shape as `useShiftDialogKeys`: the
 * capture phase, ahead of react-aria's own field handling, and from the value
 * field the apply waits one task, so it reads the value the field commits on
 * that same `Enter`. `Esc` is the modal's own dismiss.
 */
export const useDiscountDialogKeys = ({
    scope,
    onApply,
}: UseDiscountDialogKeysOptions): void => {
    const isScopeActive = useIsShortcutScopeActive(scope);
    const onApplyRef = React.useRef(onApply);
    onApplyRef.current = onApply;

    React.useEffect(() => {
        let pendingApply: ReturnType<typeof setTimeout> | undefined;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                event.key !== 'Enter' ||
                isIgnorableKeydown(event) ||
                hasModifier(event) ||
                !isScopeActive() ||
                event.target instanceof HTMLButtonElement
            ) {
                return;
            }
            event.preventDefault();
            if (event.target instanceof HTMLInputElement) {
                clearTimeout(pendingApply);
                pendingApply = setTimeout(() => onApplyRef.current(), 0);
                return;
            }
            onApplyRef.current();
        };
        window.addEventListener('keydown', handleKeyDown, true);
        return () => {
            clearTimeout(pendingApply);
            window.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [isScopeActive]);
};
